import { EventEmitter } from 'events';
import { Socket } from 'socket.io-client/build/esm/socket';
import { IAuthToken } from '../models/auth';
import config from '../services/config';
import { store } from '../store/store';

let instance: NodeServer | null = null;

type NodeServerCallback = (connected: boolean, socket: Socket) => void;

class NodeServer extends EventEmitter {
  isConnected: boolean;
  connectAttempts: number;
  socket: Socket;
  connectionListeners: Array<any>;

  constructor() {
    super();
    this.handleDisconnect = this.handleDisconnect.bind(this);
    this.connectToNode = this.connectToNode.bind(this);
    this.addConnectionListener = this.addConnectionListener.bind(this);
    this.handleConnect = this.handleConnect.bind(this);
    this.handleError = this.handleError.bind(this);
  }

  // Singleton instance getter
  static get(): NodeServer {
    if (instance == null) {
      instance = new this();
      instance.init();
    }

    return instance;
  }

  // Initialize the line monitor
  init() {
    this.isConnected = false;
    this.connectAttempts = 0;

    this.connectionListeners = [];
    this.socket = null;

    setTimeout(() => {
      this.connectToNode();
    }, 500);

    return this;
  }

  // Handle a disconnection from the node server
  handleDisconnect() {
    console.log(
      'NodeServer: Connection to server failed or disconnected. Attempting reconnect'
    );
    for (let callback of this.connectionListeners) {
      callback(false, this.connectAttempts);
    }
    this.isConnected = false;
  }

  // Attempt to connect to the node server
  connectToNode() {
    console.log('NodeServer: connecting');
    if (config.nodeServer == null) {
      console.log('NodeServer: disabled');
      return this;
    }

    if (this.socket) {
      console.log('NodeServer: socket exists');
      return this;
    }

    let callback: NodeServerCallback;
    this.connectAttempts++;
    console.log(
      `NodeServer: Connecting to server ${config.nodeServer} (${this.connectAttempts})`
    );

    const appState = store.getState();

    if (appState.common?.facility?.id == null) {
      console.log('NodeServer: awaiting facility data');
      return this;
    }

    this.socket = window.io.connect(config.nodeServer, {
      timeout: 6000,
      query: {
        'X-Facility-ID': appState.common.facility.id
      }
    });
    this.socket.on('error', (err) => {
      console.log(`NodeServer: Socket.IO error: ${err}`);
      if (!this.isConnected) {
        this.handleDisconnect();
      }
    });
    this.socket.on('connect', () => {
      console.log('NodeServer: Connected to server');
      for (callback of this.connectionListeners) {
        callback(true, this.socket);
      }
      this.isConnected = true;
      this.connectAttempts = 0;
    });
    this.socket.on('connect_failed', this.handleDisconnect);
    this.socket.on('reconnect_failed', this.handleDisconnect);
    this.socket.on('reconnecting', () => {
      console.log(
        'NodeServer: Connection to server dropped. Automatic reconnection attempt'
      );
      for (callback of this.connectionListeners) {
        callback(false, this.socket);
      }
      this.connectAttempts++;
      this.isConnected = false;
    });
    this.socket.on('station-status.change', (data) =>
      this.emit('station-status.change', data)
    );
    this.socket.on('station-status.list', (data) =>
      this.emit('station-status.list', data)
    );

    // Relay all events to the store
    const originalOnevent = (this.socket as any).onevent;
    (this.socket as any).onevent = (packet) => {
      store.dispatch({
        type: `NODE_SERVER/${packet.data[0].toUpperCase()}`,
        data: packet.data.length > 1 ? packet.data[1] : null
      });

      // Relay the original event
      originalOnevent.call(this.socket, packet);
    };

    if (appState.auth.loggedIn) {
      this.authenticate(appState.auth.token);
    }

    return this.socket;
  }

  // Add a listener for connection status changes
  addConnectionListener(callback: NodeServerCallback) {
    if (this.isConnected) {
      callback(true, this.socket);
    }

    this.connectionListeners.push(callback);
    return this;
  }

  // Remove a listener for connection status changes
  removeConnectionListener(callback: NodeServerCallback) {
    this.connectionListeners.splice(
      this.connectionListeners.indexOf(callback),
      1
    );
    return this;
  }

  // Handle the socket connection
  handleConnect(data) {
    return this;
  }

  // Handle the socket error
  handleError() {
    return this;
  }

  authenticate(accessToken: IAuthToken) {
    if (!this.socket) {
      this.connectToNode();
    } else {
      this.socket.emit('authenticate', `Bearer ${accessToken.accessToken}`);
    }
  }

  disconnect() {
    if (this.socket) {
      this.socket.emit('unauthenticate');
    }
  }
}

export default NodeServer;
